import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSession } from '../lib/session';
import { BungieError } from '../lib/bungie';
import { findPlayer, Hub, signedInPlayer, type HubChar, type Player } from './hub';
import { triumphDefs, type TriumphDefs } from './triumphs';
import { availableCards, loadMilestones, loadSeason, type Operation, type SeasonInfo } from './operations';

/**
 * Everything the hub's pages share: who is shown, their live profile, the triumph definitions, this week's
 * activities and the season. Loaded when the hub opens or Refresh is clicked, never in the background (as the app).
 */

export interface HubState {
  player?: Player;
  hub?: Hub;
  defs?: TriumphDefs;
  char?: HubChar;
  setChar: (id: string) => void;
  ops: Operation[];
  cards: { kind: string; pages: Operation[] }[];
  season?: SeasonInfo | null;
  status: string;
  error?: string;
  loading: boolean;
  refresh: () => void;
  /** Bump to make private pages read again after something moved. */
  stamp: number;
  touch: () => void;
  signedIn: boolean;
  /** The signed-in player is the one shown (so private pages can be read). */
  own: boolean;
  getToken: () => Promise<string | undefined>;
  lookup: (name: string) => Promise<void>;
  lookupError?: string;
  clearLookup: () => void;
}

const Ctx = createContext<HubState | null>(null);
export const useHub = () => {
  const h = useContext(Ctx);
  if (!h) throw new Error('useHub outside HubProvider');
  return h;
};

const LOOKUP_KEY = 'd2c.lookup';
const readLookup = () => { try { return localStorage.getItem(LOOKUP_KEY) ?? ''; } catch { return ''; } };
const saveLookup = (v: string) => { try { if (v) localStorage.setItem(LOOKUP_KEY, v); else localStorage.removeItem(LOOKUP_KEY); } catch { /* storage blocked */ } };

export function HubProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [player, setPlayer] = useState<Player>();
  const [own, setOwn] = useState(false);
  const [hub, setHub] = useState<Hub>();
  const [defs, setDefs] = useState<TriumphDefs>();
  const [charId, setCharId] = useState<string>();
  const [ops, setOps] = useState<Operation[]>([]);
  const [cards, setCards] = useState<{ kind: string; pages: Operation[] }[]>([]);
  const [season, setSeason] = useState<SeasonInfo | null>();
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [turn, setTurn] = useState(0);
  const [stamp, setStamp] = useState(0);
  const [lookupName, setLookupName] = useState(readLookup);
  const [lookupError, setLookupError] = useState<string>();

  // who to show: a looked-up player when one was asked for, else the signed-in player
  useEffect(() => {
    if (session.status === 'loading') return;
    let live = true;
    (async () => {
      try {
        if (lookupName) {
          const p = await findPlayer(lookupName);
          if (!live) return;
          setPlayer(p); setLookupError(undefined);
          let mine = false;
          if (session.status === 'signedIn') {
            const t = await session.getToken();
            if (t) mine = (await signedInPlayer(t).catch(() => undefined))?.membershipId === p.membershipId;
          }
          if (live) setOwn(mine);
        } else if (session.status === 'signedIn') {
          const t = await session.getToken();
          if (!t) return;
          const p = await signedInPlayer(t);
          if (live) { setPlayer(p); setOwn(true); }
        } else if (live) { setPlayer(undefined); setOwn(false); }
      } catch (e) {
        if (!live) return;
        if (lookupName) { setLookupError(e instanceof Error ? e.message : String(e)); setLookupName(''); saveLookup(''); }
        else setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { live = false; };
  }, [session.status, session.getToken, lookupName]);

  // the profile, this week's milestones and the triumph definitions (nobody to show: only the milestones, which are public)
  useEffect(() => {
    if (!player) {
      setHub(undefined);
      if (session.status !== 'signedOut') return;   // signed in: the hub's own read is coming
      let live = true;
      loadMilestones().then((o) => live && setOps(o)).catch(() => undefined);
      return () => { live = false; };
    }
    let live = true;
    (async () => {
      setLoading(true);
      setStatus('Asking Bungie for your Guardian...');
      try {
        const token = session.status === 'signedIn' ? await session.getToken() : undefined;
        const defsJob = triumphDefs().catch(() => undefined);
        const opsJob = loadMilestones().catch(() => [] as Operation[]);   // the front page works without it
        const h = await Hub.load(player, token);
        if (!live) return;
        setHub(h);
        setCharId((id) => (h.chars.some((c) => c.id === id) ? id : h.chars[0]?.id));
        setError(undefined);
        const time = h.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        setStatus(h.hasRecords ? `Updated ${time}` : `Updated ${time}. This player's privacy settings hide their triumphs.`);
        const [d, o] = await Promise.all([defsJob, opsJob]);
        if (!live) return;
        setDefs(d);
        setOps(o);
      } catch (e) {
        if (!live) return;
        const why = e instanceof BungieError ? e.message : `Something went wrong (${e instanceof Error ? e.message : e}).`;
        setStatus('Could not load.');
        setError(why);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [player, turn, session.status === 'signedOut']);   // eslint-disable-line react-hooks/exhaustive-deps

  const char = hub?.chars.find((c) => c.id === charId) ?? hub?.chars[0];

  // what this character can launch, and the season (both follow the character)
  useEffect(() => {
    if (!hub || !char) return;
    let live = true;
    availableCards(hub.activities[char.id] ?? []).then((c) => live && setCards(c)).catch(() => live && setCards([]));
    loadSeason(hub, char.id).then((s) => live && setSeason(s)).catch(() => live && setSeason(null));
    return () => { live = false; };
  }, [hub, char?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = useCallback(async (name: string) => {
    setLookupError(undefined);
    try {
      await findPlayer(name);
      saveLookup(name.trim());
      setLookupName(name.trim());
    } catch (e) {
      setLookupError(e instanceof Error ? e.message : String(e));
    }
  }, []);
  const clearLookup = useCallback(() => { saveLookup(''); setLookupName(''); }, []);

  const value = useMemo<HubState>(() => ({
    player, hub, defs, char, setChar: setCharId, ops, cards, season, status, error, loading,
    refresh: () => setTurn((t) => t + 1), stamp, touch: () => setStamp((s) => s + 1),
    signedIn: session.status === 'signedIn', own, getToken: session.getToken, lookup, lookupError, clearLookup,
  }), [player, hub, defs, char, ops, cards, season, status, error, loading, stamp, session.status, own, session.getToken, lookup, lookupError, clearLookup]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// private reads shared by every page that shows them (the Inventory, the Vault and the front page all show the stash)
const privateCache = new Map<string, Promise<unknown>>();

/** The shared read for this player, key and stamp: started now if nobody has asked for it yet. */
function privateJob<T>(id: string, own: boolean, getToken: () => Promise<string | undefined>, load: (token: string) => Promise<T>, isPublic?: boolean): Promise<T> {
  let job = privateCache.get(id) as Promise<T> | undefined;
  if (!job) {
    job = (async () => {
      const t = own ? await getToken() : undefined;
      if (!t && !isPublic) throw new BungieError(0, 'SignedOut', 'Your Bungie sign-in has run out. Sign in again.');
      return load(t ?? '');
    })();
    privateCache.set(id, job);
    job.catch(() => privateCache.delete(id));
  }
  return job;
}

/**
 * Starts a page's read as soon as the player is known, alongside the hub's own (the page itself waits for the hub, so
 * its read would only begin after it): the page's usePrivate then finds it on the way. Same key and load as the page.
 */
export function usePrefetch<T>(key: string | undefined, load: (token: string) => Promise<T>, opts: { public?: boolean } = {}) {
  const { own, getToken, stamp, player } = useHub();
  useEffect(() => {
    if ((!own && !opts.public) || !key || !player) return;
    void privateJob(`${player.membershipId}|${key}|${stamp}`, own, getToken, load, opts.public).catch(() => undefined);
  }, [own, key, stamp, player?.membershipId]);   // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Reads something private for the shown player (needs the sign-in). Pages asking for the same key share one request;
 * it is read again when the hub's stamp changes (after a move) or "again" is called.
 */
export function usePrivate<T>(key: string | undefined, load: (token: string) => Promise<T>, opts: { public?: boolean } = {}) {
  const { own, getToken, stamp, player } = useHub();
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [turn, setTurn] = useState(0);
  const shownFor = useRef('');   // whose / which data is on screen: only a change of that clears it
  useEffect(() => {
    if ((!own && !opts.public) || !key || !player) { setData(undefined); shownFor.current = ''; return; }
    let live = true;
    const job = privateJob(`${player.membershipId}|${key}|${stamp}`, own, getToken, load, opts.public);
    // read again after a move or Refresh (stamp, again): the last answer stays on screen until the new one arrives
    // (clearing it made pages flicker empty, e.g. 3D View's gear 30 s after a swap); another player or key clears it
    const what = `${player.membershipId}|${key}|${own}`;
    if (shownFor.current !== what) { setData(undefined); shownFor.current = what; }
    setError(undefined);
    job.then((v) => live && setData(v)).catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => { live = false; };
  }, [own, key, stamp, turn, player?.membershipId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const again = () => {
    if (player && key) privateCache.delete(`${player.membershipId}|${key}|${stamp}`);
    setTurn((t) => t + 1);
  };
  return { data, error, again };
}
